import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRightOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import honestiIqImage from "../../assets/showcase/honesti-iq-dashboard.svg";
import honestiIqLogo from "../../assets/showcase/honesti-iq-logo.png";
import easyonImage from "../../assets/showcase/easyon-workflow.svg";
import easyonLogo from "../../assets/showcase/easyon-logo.png";
import whatsappApiImage from "../../assets/showcase/whatsapp-api-messaging.svg";
import whatsappBulkImage from "../../assets/showcase/whatsapp-bulk-campaign.svg";
import socialAutomationImage from "../../assets/showcase/social-automation-analytics.svg";

const showcaseProducts = [
    {
        id: "honesti-iq",
        name: "HonestiIQ",
        subtitle: "Customer Growth Platform",
        description:
            "Unify leads, pipeline stages, and revenue analytics in one CRM command center built for teams that need full visibility from first contact to closed deal.",
        features: ["Pipeline & lead tracking", "Revenue analytics dashboard", "Automated follow-up workflows"],
        image: honestiIqImage,
        imageAlt: "HonestiIQ CRM dashboard with pipeline analytics and performance metrics",
        logoOverlay: honestiIqLogo,
        logoOverlayAlt: "HonestiIQ logo",
        path: "/software/honesti-crm",
    },
    {
        id: "easyon",
        name: "EasyOn",
        subtitle: "Business Operations Automation",
        description:
            "Automate quotations, billing, inventory, and compliance workflows across teams and locations in one operations workspace designed for growing businesses.",
        features: ["Quotation & billing automation", "Inventory management", "Multi-company operations"],
        image: easyonImage,
        imageAlt: "EasyOn business workflow automation dashboard",
        logoOverlay: easyonLogo,
        logoOverlayAlt: "EasyOn logo",
        logoOverlayVariant: "wide",
        path: "/software/easyon",
    },
    {
        id: "whatsapp-api",
        name: "WhatsApp API",
        subtitle: "Official Business Messaging",
        description:
            "Deliver secure, scalable customer conversations on WhatsApp with rich media, automated replies, and seamless agent handoffs for modern support teams.",
        features: ["Official WhatsApp Business API", "Rich media messaging", "Agent inbox & automation"],
        image: whatsappApiImage,
        imageAlt: "WhatsApp API customer support and messaging interface",
        logoOverlay: honestiIqLogo,
        logoOverlayAlt: "HonestiIQ logo",
        path: "/software/whatsapp-api-platform",
    },
    {
        id: "whatsapp-bulk",
        name: "WhatsApp Bulk Sender",
        subtitle: "Campaign Distribution",
        description:
            "Launch high-volume WhatsApp campaigns with audience segmentation, delivery tracking, and performance reporting to reach customers at scale.",
        features: ["Audience segmentation", "Campaign delivery tracking", "Template-based broadcasting"],
        image: whatsappBulkImage,
        imageAlt: "WhatsApp bulk campaign marketing dashboard",
        logoOverlay: honestiIqLogo,
        logoOverlayAlt: "HonestiIQ logo",
        path: "/software/whatsapp-bulk-sender",
    },
    {
        id: "social-automation",
        name: "Social Media Automation",
        subtitle: "Cross-Channel Growth",
        description:
            "Plan, publish, and measure social content across channels from a single dashboard that turns engagement into measurable business growth.",
        features: ["Cross-channel publishing", "Engagement automation", "Performance analytics"],
        image: socialAutomationImage,
        imageAlt: "Social media automation analytics dashboard",
        logoOverlay: honestiIqLogo,
        logoOverlayAlt: "HonestiIQ logo",
        path: "/software/socialmedia-automation-software",
    },
];

const slideVariants = {
    enter: (direction: number) => ({
        x: direction > 0 ? 56 : -56,
        opacity: 0,
    }),
    center: {
        x: 0,
        opacity: 1,
    },
    exit: (direction: number) => ({
        x: direction > 0 ? -56 : 56,
        opacity: 0,
    }),
};

export const ProductShowcaseCarousel = () => {
    const navigate = useNavigate();
    const [activeIndex, setActiveIndex] = useState(0);
    const [direction, setDirection] = useState(1);

    const goTo = useCallback((index: number, nextDirection: number) => {
        setDirection(nextDirection);
        setActiveIndex(index);
    }, []);

    const goNext = useCallback(() => {
        setDirection(1);
        setActiveIndex((current) => (current + 1) % showcaseProducts.length);
    }, []);

    const goPrev = useCallback(() => {
        setDirection(-1);
        setActiveIndex((current) => (current - 1 + showcaseProducts.length) % showcaseProducts.length);
    }, []);

    useEffect(() => {
        const timer = window.setInterval(goNext, 5000);
        return () => window.clearInterval(timer);
    }, [activeIndex, goNext]);

    const product = showcaseProducts[activeIndex];

    return (
        <div id="product-showcase" className="product-showcase">
            <div className="product-showcase__shell">
                <button
                    type="button"
                    className="product-showcase__nav product-showcase__nav--prev"
                    onClick={goPrev}
                    aria-label="Previous product"
                >
                    <LeftOutlined />
                </button>

                <div className="product-showcase__viewport">
                    <AnimatePresence custom={direction} mode="wait">
                        <motion.article
                            key={product.id}
                            className="product-showcase__card"
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <div className="product-showcase__media">
                                <img
                                    src={product.image}
                                    alt={product.imageAlt}
                                    className="product-showcase__image"
                                    loading="lazy"
                                    draggable={false}
                                />
                                {"logoOverlay" in product && product.logoOverlay ? (
                                    <div
                                        className={[
                                            "product-showcase__logo-overlay",
                                            "logoOverlayPosition" in product && product.logoOverlayPosition === "top"
                                                ? "product-showcase__logo-overlay--top"
                                                : "",
                                            "logoOverlayVariant" in product && product.logoOverlayVariant === "wide"
                                                ? "product-showcase__logo-overlay--wide"
                                                : "",
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                        aria-hidden="true"
                                    >
                                        <img
                                            src={product.logoOverlay}
                                            alt={product.logoOverlayAlt ?? ""}
                                            className="product-showcase__logo"
                                            loading="lazy"
                                            draggable={false}
                                        />
                                    </div>
                                ) : null}
                            </div>
                            <div className="product-showcase__content">
                                <h4 className="product-showcase__title">{product.name}</h4>
                                <p className="product-showcase__subtitle">{product.subtitle}</p>
                                <p className="product-showcase__desc">{product.description}</p>
                                <ul className="product-showcase__features">
                                    {product.features.map((feature) => (
                                        <li key={feature}>{feature}</li>
                                    ))}
                                </ul>
                                <Button
                                    type="primary"
                                    className="premium-btn-primary product-showcase__cta"
                                    onClick={() => navigate(product.path)}
                                >
                                    Explore Product <ArrowRightOutlined />
                                </Button>
                            </div>
                        </motion.article>
                    </AnimatePresence>
                </div>

                <button
                    type="button"
                    className="product-showcase__nav product-showcase__nav--next"
                    onClick={goNext}
                    aria-label="Next product"
                >
                    <RightOutlined />
                </button>
            </div>

            <div className="product-showcase__dots" role="tablist" aria-label="Product showcase slides">
                {showcaseProducts.map((item, index) => (
                    <button
                        key={item.id}
                        type="button"
                        role="tab"
                        aria-selected={activeIndex === index}
                        aria-label={`Show ${item.name}`}
                        className={`product-showcase__dot ${activeIndex === index ? "is-active" : ""}`}
                        onClick={() => goTo(index, index > activeIndex ? 1 : -1)}
                    />
                ))}
            </div>
        </div>
    );
};
